import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { InstallButton } from '@/context/InstallContext';

export default function Register() {
  const navigate = useNavigate();
  const { login } = useContext(AuthContext);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    verify_password: '',
    address: '',
    state: '',
    lga: '',
    referral_code: '',
    disclaimer_accepted: false
  });
  const [states, setStates] = useState([]);
  const [lgas, setLgas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDisclaimer, setShowDisclaimer] = useState(false);

  useEffect(() => {
    fetchStates();
  }, []);

  useEffect(() => {
    if (formData.state) {
      fetchLgas(formData.state);
    }
  }, [formData.state]);

  const fetchStates = async () => {
    try {
      const response = await axios.get(`${API}/locations/states`);
      setStates(response.data.states);
    } catch (error) {
      console.error('Failed to fetch states');
    }
  };

  const fetchLgas = async (state) => {
    try {
      const response = await axios.get(`${API}/locations/lgas/${state}`);
      setLgas(response.data.lgas);
    } catch (error) {
      console.error('Failed to fetch LGAs');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.disclaimer_accepted) {
      toast.error('Please read and accept the service disclaimer');
      setShowDisclaimer(true);
      return;
    }
    
    if (formData.password !== formData.verify_password) {
      toast.error('Passwords do not match');
      return;
    }
    
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/register`, formData);
      login(response.data.token, response.data.user);
      toast.success('Account created successfully!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Top bar with Install Button */}
      <div className="bg-primary text-white py-2 px-4">
        <div className="max-w-md mx-auto flex justify-between items-center">
          <span className="text-sm font-medium">Excel Era Auto Care</span>
          <InstallButton variant="header" size="sm" />
        </div>
      </div>
      
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-white border-2 border-border rounded-sm shadow-hard p-8">
            <div className="text-center mb-8">
              <img 
                src="https://customer-assets.emergentagent.com/job_vehicle-maintenance-21/artifacts/76wdsgvy_WhatsApp%20Image%202025-12-16%20at%2017.28.15.jpeg" 
                alt="Excel Era Auto Care" 
                className="h-16 w-16 object-contain mx-auto mb-4"
              />
              <h1 className="text-3xl font-heading font-bold text-foreground">Get Started</h1>
              <p className="text-muted-foreground font-medium mt-2">Create your account</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <Label htmlFor="name" className="font-bold uppercase text-sm tracking-wide">Full Name</Label>
                <Input
                  id="name"
                  type="text"
                  required
                  data-testid="register-name-input"
                  className="input-field mt-2"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>

            <div>
              <Label htmlFor="email" className="font-bold uppercase text-sm tracking-wide">Email</Label>
              <Input
                id="email"
                type="email"
                required
                data-testid="register-email-input"
                className="input-field mt-2"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </div>

            <div>
              <Label htmlFor="phone" className="font-bold uppercase text-sm tracking-wide">Phone Number</Label>
              <Input
                id="phone"
                type="tel"
                required
                data-testid="register-phone-input"
                className="input-field mt-2"
                placeholder="+234 XXX XXX XXXX"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="font-bold uppercase text-sm tracking-wide">State</Label>
                <Select value={formData.state} onValueChange={(v) => setFormData({ ...formData, state: v, lga: '' })}>
                  <SelectTrigger className="input-field mt-2" data-testid="register-state-select">
                    <SelectValue placeholder="Select state" />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    {states.map(state => (
                      <SelectItem key={state} value={state}>{state}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="font-bold uppercase text-sm tracking-wide">LGA</Label>
                <Select value={formData.lga} onValueChange={(v) => setFormData({ ...formData, lga: v })} disabled={!formData.state}>
                  <SelectTrigger className="input-field mt-2" data-testid="register-lga-select">
                    <SelectValue placeholder="Select LGA" />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    {lgas.map(lga => (
                      <SelectItem key={lga} value={lga}>{lga}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="address" className="font-bold uppercase text-sm tracking-wide">Service Address</Label>
              <Input
                id="address"
                required
                data-testid="register-address-input"
                className="input-field mt-2"
                placeholder="Your detailed address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              />
            </div>

            <div>
              <Label htmlFor="password" className="font-bold uppercase text-sm tracking-wide">Password</Label>
              <Input
                id="password"
                type="password"
                required
                data-testid="register-password-input"
                className="input-field mt-2"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              />
            </div>

            <div>
              <Label htmlFor="verify_password" className="font-bold uppercase text-sm tracking-wide">Confirm Password</Label>
              <Input
                id="verify_password"
                type="password"
                required
                data-testid="register-verify-password-input"
                className="input-field mt-2"
                value={formData.verify_password}
                onChange={(e) => setFormData({ ...formData, verify_password: e.target.value })}
              />
            </div>

            <div>
              <Label htmlFor="referral_code" className="font-bold uppercase text-sm tracking-wide">
                Referral Code <span className="text-muted-foreground font-normal">(Optional)</span>
              </Label>
              <Input
                id="referral_code"
                type="text"
                data-testid="register-referral-input"
                className="input-field mt-2"
                placeholder="Enter code if referred by a marketer"
                value={formData.referral_code}
                onChange={(e) => setFormData({ ...formData, referral_code: e.target.value.toUpperCase() })}
                maxLength={6}
              />
              <p className="text-xs text-muted-foreground mt-1">If someone referred you, enter their code here</p>
            </div>

            {/* Disclaimer Section */}
            <div className="border-2 border-border rounded-sm p-4 bg-yellow-50">
              <div className="flex items-start gap-3 mb-3">
                <svg className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                <div className="flex-1">
                  <h3 className="font-bold text-sm text-yellow-800 mb-1 uppercase tracking-wide">Important: Service Disclaimer</h3>
                  <button
                    type="button"
                    onClick={() => setShowDisclaimer(!showDisclaimer)}
                    className="text-sm text-primary font-bold hover:underline"
                    data-testid="view-disclaimer-btn"
                  >
                    {showDisclaimer ? '▼ Hide Disclaimer' : '► Read Full Disclaimer (Required)'}
                  </button>
                </div>
              </div>

              {showDisclaimer && (
                <div className="max-h-64 overflow-y-auto bg-white border-2 border-border rounded p-4 text-sm text-foreground mb-4 space-y-3">
                  <h3 className="font-bold text-base">Vehicle Service Disclaimer</h3>
                  
                  <div>
                    <h4 className="font-bold">1. Pre-Existing Condition & Service History</h4>
                    <p className="text-xs">Our company provides vehicle inspection, maintenance, and repair services based on the condition of the vehicle at the time it is presented to us. We are not responsible for issues arising from the vehicle's prior service history, including but not limited to incomplete, irregular, or missing service records; poor workmanship by previous service providers; use of non-genuine, incompatible, or substandard parts prior to our service; undiagnosed mechanical, electrical, or structural issues existing before our inspection. Any damage, wear, or malfunction caused by past neglect or improper maintenance remains the responsibility of the vehicle owner.</p>
                  </div>

                  <div>
                    <h4 className="font-bold">2. Delayed or Missed Maintenance</h4>
                    <p className="text-xs">Failure to service a vehicle at manufacturer-recommended intervals may result in reduced engine performance and efficiency, accelerated wear of engine, transmission, suspension, and braking components, increased risk of breakdowns or mechanical failure, and reduced engine longevity and reliability. Our company shall not be held liable for failures or performance issues that occur due to delayed, skipped, or improper maintenance prior to our involvement.</p>
                  </div>

                  <div>
                    <h4 className="font-bold">3. Impact on Engine Longevity & Performance</h4>
                    <p className="text-xs">The longevity, performance, and reliability of a vehicle's engine and components depend on timely servicing and oil changes, proper driving habits, fuel quality, environmental conditions, and previous maintenance practices. While we perform services to professional standards, we cannot guarantee restoration of original performance or lifespan if the vehicle has a history of neglect, abuse, or inconsistent servicing before we begin work.</p>
                  </div>

                  <div>
                    <h4 className="font-bold">4. Limited Scope of Inspection</h4>
                    <p className="text-xs">Our inspections are visual and functional only, limited to the services requested or authorized. We do not dismantle components unless specifically approved. As a result, hidden, internal, or intermittent issues may not be identified, and some problems may surface after service due to normal wear or pre-existing conditions. Discovery of additional issues during or after service does not imply fault or negligence on our part.</p>
                  </div>

                  <div>
                    <h4 className="font-bold">5. No Guarantee Against Future Failures</h4>
                    <p className="text-xs">Servicing or repairing one component does not guarantee that other components will not fail in the future. Vehicles are complex mechanical systems, and unrelated parts may fail due to age, mileage, or prior condition.</p>
                  </div>

                  <div>
                    <h4 className="font-bold">6. Parts & Repairs Disclaimer</h4>
                    <p className="text-xs">Replacement parts may be OEM or aftermarket, as agreed with the customer. Used or refurbished parts (if approved) carry limited or no warranty. Performance improvements are not guaranteed, especially on high-mileage or poorly maintained vehicles.</p>
                  </div>

                  <div>
                    <h4 className="font-bold">7. Customer Responsibility</h4>
                    <p className="text-xs">The vehicle owner is responsible for providing accurate information regarding vehicle history and symptoms, approving recommended repairs in writing, and following post-service care instructions. Failure to follow recommendations may result in further damage, for which our company is not responsible.</p>
                  </div>

                  <div>
                    <h4 className="font-bold">8. Limitation of Liability</h4>
                    <p className="text-xs">To the fullest extent permitted by law, our company shall not be liable for indirect, incidental, or consequential damages, loss of vehicle use, income, or business, or engine or component failure caused by pre-existing conditions or delayed servicing. Our liability, if any, is limited strictly to the value of the service performed.</p>
                  </div>

                  <div>
                    <h4 className="font-bold">9. Acceptance of Disclaimer</h4>
                    <p className="text-xs">By authorizing service, the customer acknowledges that they have read, understood, and accepted this disclaimer and agree to its terms and conditions.</p>
                  </div>
                </div>
              )}

              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  required
                  checked={formData.disclaimer_accepted}
                  onChange={(e) => setFormData({ ...formData, disclaimer_accepted: e.target.checked })}
                  data-testid="disclaimer-checkbox"
                  className="mt-1 w-5 h-5 border-2 border-foreground rounded"
                />
                <span className="text-sm text-foreground">
                  I have read and agree to the <span className="font-bold">Vehicle Service Disclaimer</span>
                </span>
              </label>
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid="register-submit-btn"
              className="btn-primary w-full h-12 text-lg"
            >
              {loading ? 'Creating Account...' : 'Create Account'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-muted-foreground font-medium">
              Already have an account?{' '}
              <Link to="/login" className="text-primary font-bold hover:underline" data-testid="register-login-link">
                Sign in
              </Link>
            </p>
          </div>

          <div className="mt-4 text-center">
            <Link to="/" className="text-sm text-muted-foreground hover:text-foreground font-medium" data-testid="register-back-link">
              ← Back to home
            </Link>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}